// Lazy image thumbnail for the Media Center. Streams the file's binary
// content via `/admin/files/{id}/content` (auth required) and renders it
// using `URL.createObjectURL`. Non-image MIME types fall back to a file icon.

import { useEffect, useState } from 'react';
import { File as FileIcon, FileText, Image as ImageIcon, FileVideo, FileAudio } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { filesApi } from '@api/files';
import { isImageMime } from '../utils';

interface FileThumbnailProps {
  id: string;
  fileName: string;
  mimeType: string;
  size?: number;
  /** Lazy = only fetch when the thumb scrolls into view. */
  lazy?: boolean;
}

function FallbackIcon({ mimeType, size }: { mimeType: string; size: number }) {
  const cls = mimeType.startsWith('video/')
    ? FileVideo
    : mimeType.startsWith('audio/')
      ? FileAudio
      : mimeType.startsWith('text/') || mimeType.includes('pdf')
        ? FileText
        : mimeType.startsWith('image/')
          ? ImageIcon
          : FileIcon;
  const Icon = cls;
  return <Icon size={size} strokeWidth={1.5} />;
}

export function FileThumbnail({ id, fileName, mimeType, size = 64, lazy = true }: FileThumbnailProps) {
  const { t } = useTranslation('media');
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isImageMime(mimeType, fileName)) return undefined;
    let active = true;
    let createdUrl: string | null = null;
    const load = async () => {
      try {
        const blob = await filesApi.download(id);
        if (!active) return;
        createdUrl = URL.createObjectURL(blob);
        setUrl(createdUrl);
      } catch {
        if (active) setFailed(true);
      }
    };
    if (lazy) {
      // small defer so we don't slam the BE on initial paint
      const t = window.setTimeout(() => {
        void load();
      }, 50);
      return () => {
        active = false;
        window.clearTimeout(t);
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
    }
    void load();
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [id, mimeType, fileName, lazy]);

  if (!isImageMime(mimeType, fileName) || failed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--ds-bg-muted, #f1f5f9)',
          borderRadius: 8,
          color: 'var(--ds-text-tertiary, #64748b)',
        }}
        aria-label={t('thumbnail.fileIcon')}
      >
        <FallbackIcon mimeType={mimeType} size={Math.max(20, Math.floor(size / 2.5))} />
      </div>
    );
  }

  if (!url) {
    return (
      <div
        style={{
          width: size,
          height: size,
          background: 'var(--ds-bg-muted, #f1f5f9)',
          borderRadius: 8,
        }}
        aria-label={t('thumbnail.previewLoading')}
      />
    );
  }

  return (
    <img
      src={url}
      alt={fileName}
      loading="lazy"
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        borderRadius: 8,
        background: 'var(--ds-bg-muted, #f1f5f9)',
      }}
    />
  );
}
